import React, { useMemo } from "react";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";

import { IconButton } from "@/Components/v2/atoms/Button";
import { Typography } from "@/Components/v2/atoms/Typography";
import { Controller } from "react-hook-form";

import { Dialog, DialogContent } from "@/Components/v2/atoms/Dialog";
import { Stack } from "@/Components/v2/atoms/Stack";

import { ActionButtons } from "./ActionButtons";
import { useActionPlanForm } from "./hooks/useActionPlanForm";
import { useScenarioData } from "./hooks/useScenarioData";
import { ScenarioDetails } from "./ScenarioDetails";
import { ScenarioSelectionField } from "./ScenarioSelectionField";
import { ScenarioOption } from "./types";
import { TextField } from "@/Components/v2/atoms/TextField";
import { useScenariosV3Routes } from "../../hooks/useScenariosV3Routes";
import { ActionPlanSummary } from "@mutiny-group/sbo-openapi-client";
import { AxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import {
  CreateScenarioResponse,
  useCreateScenario,
} from "@/pages/scenario-builder/v3/api/createScenario";
import { useFeatureGate } from "@statsig/react-bindings";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useAnalytics } from "@/hooks/useAnalytics";
import dayjs from "dayjs";
import { useEligibleActionPlanScenarios } from "../../hooks/useEligibleActionPlanScenarios";
import { NoEligibleScenarios } from "./NoEligibleScenariosState";

type NewActionPlanModalProps = {
  open: boolean;
  handleClose: () => void;
  scenarioOption?: ScenarioOption;
};

export const NewActionPlanModal = ({
  open,
  handleClose,
  scenarioOption,
}: NewActionPlanModalProps) => {
  const { eligibleScenarios: scenarioOptions, isLoading: isEligibleScenariosLoading } =
    useEligibleActionPlanScenarios();

  const { navigateToActionPlan, navigateToScenarioForm } = useScenariosV3Routes();
  const queryClient = useQueryClient();
  const { value: isScenariosApiEnabled } = useFeatureGate(featureFlag.scenarioBuilderApi);
  const { scenarioCreated, actionPlanCreated } = useAnalytics();

  const handleSuccess = async (result: ActionPlanSummary) => {
    if (!result.uuid) {
      console.error("No action plan uuid returned:", result);
      return;
    }
    actionPlanCreated({
      actionPlanId: result.uuid,
      actionPlanName: result.name,
      actionPlanLength: dayjs(result.endDate).diff(dayjs(result.startDate), "months"),
      actionPlanSpend: result.spendPlannedTotal,
      actionPlanStatus: result.status,
      actionPlanRevenueGoal: result.revenueTarget,
      actionPlanRevenueForecast: result.revenueForecastToDate,
    });

    toast.success("Action Plan created successfully!");
    handleClose();
    navigateToActionPlan(result.uuid);
    // Added so that the scenario banner is updated with the correct banner state when returning back.
    if (scenarioOption?.uuid) {
      await queryClient.invalidateQueries({ queryKey: ["scenario", scenarioOption?.uuid] });
      await queryClient.invalidateQueries({ queryKey: ["scenario-v2", scenarioOption?.uuid] });
    }
    await queryClient.invalidateQueries({ queryKey: ["action-plans"] });
  };

  const handleError = React.useCallback((error: AxiosError) => {
    toast.error("Failed to create action plan. Please try again.");
    console.error("Create action plan error:", error);
  }, []);

  const { control, handleSubmit, errors, watchedScenarioId, onSubmit, isSubmitting } =
    useActionPlanForm({
      scenarioOption,
      onSuccess: handleSuccess,
      onError: handleError,
    });

  const selectedScenario = useMemo(() => {
    return scenarioOptions.find((s) => s.uuid === watchedScenarioId);
  }, [scenarioOptions, watchedScenarioId]);

  const scenarioLabel = selectedScenario?.name;

  const { scenarioFields } = useScenarioData(selectedScenario);

  const createScenarioMutation = useCreateScenario({
    isScenariosApiEnabled,
    config: {
      onSuccess: (result: CreateScenarioResponse) => {
        if (result.uuid) {
          scenarioCreated({
            scenarioType: result.scenarioType,
            scenarioId: result.uuid,
            scenarioName: result.name || "Untitled Scenario",
            isCloned: false,
          });
          handleClose();
          navigateToScenarioForm(result.uuid);
        }
      },
    },
  });

  const handleCreateScenario = () => {
    const email = JSON.parse(localStorage.getItem("user") || "{}").email;
    if (!email) {
      window.location.reload();
      return;
    }
    createScenarioMutation.mutate({
      scenarioType: "budget",
      createdBy: email,
      name: "Untitled Scenario",
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            maxWidth: "465px",
          },
        },
      }}
    >
      <DialogContent
        sx={{
          position: "relative",
          px: 4,
        }}
      >
        {!isEligibleScenariosLoading && scenarioOptions.length === 0 ? (
          <NoEligibleScenarios
            onClick={handleCreateScenario}
            handleClose={handleClose}
            isLoading={createScenarioMutation.isPending}
          />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <>
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                  }}
                >
                  <CloseIcon />
                </IconButton>
                <Typography variant="h5" fontWeight={600}>
                  Create Action Plan
                </Typography>
                <Typography variant="body2" sx={{ mt: "4px !important" }}>
                  Please confirm the details of your new action plan.
                </Typography>
              </>

              <Stack spacing={0.5}>
                <Typography variant="body2" fontWeight={600}>
                  Action Plan Name
                </Typography>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Enter a name for your Action Plan"
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      autoComplete="off"
                    />
                  )}
                />
              </Stack>
              <ScenarioSelectionField
                control={control}
                errors={errors}
                scenarioOptions={scenarioOptions}
                scenarioLabel={scenarioLabel ?? ""}
                isLoading={isEligibleScenariosLoading}
              />
              <ScenarioDetails scenarioFields={scenarioFields} />
              <ActionButtons isSubmitting={isSubmitting} onCancel={handleClose} />
            </Stack>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
